import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { zodRemoveNullable } from '@/lib/zod-helper';
import { smartFormApi } from '@/services';
import type { ApiResponse } from '@/types/api-types';
import type { QuerySuspenseOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/smart-form/api-schema';
import {
  actionsSchema,
  queryPatientEducationQuerySchema,
  queryPatientEducationResSchema,
} from '~/generated/smart-form/zod-schema';

const API_PATH = '/v1/education/queryPatientEducation' as const;
export const QUERY_PATIENT_EDUCATION_KEY = API_PATH;
type APIResponse = ApiResponse<paths, typeof API_PATH>;

const patientEducationItemSchema = zodRemoveNullable(
  queryPatientEducationResSchema,
  {
    excludeMask: {
      signature: true,
      order: true,
      cancel: true,
    },
  },
).extend({
  actions: zodRemoveNullable(actionsSchema),
});

const patientEducationListSchema = z.array(patientEducationItemSchema);

export type PatientEducationItem = z.infer<typeof patientEducationItemSchema>;
export type PatientEducationList = z.infer<typeof patientEducationListSchema>;
type QueryPatientEducationParams = z.infer<
  typeof queryPatientEducationQuerySchema
>;

const queryFn = async (
  params: QueryPatientEducationParams,
  signal: AbortSignal,
): Promise<PatientEducationList> => {
  const { data: response } = await smartFormApi.get<APIResponse>(API_PATH, {
    params: queryPatientEducationQuerySchema.parse(params),
    signal,
  });
  return patientEducationListSchema.parse(response.data);
};

export const getSuspenseQueryPatientEducationOptions = (
  params: QueryPatientEducationParams,
) =>
  queryOptions({
    queryKey: [QUERY_PATIENT_EDUCATION_KEY, params] as const,
    queryFn: ({ signal }) => queryFn(params, signal),
  });

export const useSuspenseQueryPatientEducation = (
  params: QueryPatientEducationParams,
  options?: QuerySuspenseOptions<
    PatientEducationList,
    readonly [typeof QUERY_PATIENT_EDUCATION_KEY, QueryPatientEducationParams]
  >,
) =>
  useSuspenseQuery({
    ...getSuspenseQueryPatientEducationOptions(params),
    ...options,
  });
